/**
 * Phase E5: who is on an event, as chips you can tap.
 *
 * No imports, on purpose — the same contract `affinity.ts`, `filmRank.ts`,
 * `runPattern.ts` and `place.ts` keep, so the Worker's offline suite can load
 * this file directly and `npm test` can gate the rules. `npm test` cannot see
 * the layout these rules are drawn in; it can see which chip belongs in which
 * group, in which order, and what a fact line says.
 *
 * The shapes below are declared structurally rather than imported from
 * `types.ts` for that reason: `feed_detail`'s `people` and `ensembles` are
 * what E1 and E3 put in the view, and this file states the columns it reads
 * instead of dragging `database.types.ts` into a test.
 *
 * **Nothing here writes a sentence.** K8: a fact line is Wikidata's own
 * description (or Discogs' own first sentence, `bio`), Wikidata's own years
 * and, for a DJ, two of Discogs' styles — joined. No model writes a
 * biography, and this file composes nothing beyond the join.
 */

export type Kind = 'person' | 'ensemble'

export interface Chip {
  kind: Kind
  id: string
  name: string
  role: string
  /** `factLine`'s answer — null for everyone neither Wikidata (E2) nor the
   *  DJ facts pass (Discogs, MusicBrainz) has anything on. */
  fact?: string | null
  /** `people.wikipedia_url`. Null on an ensemble: E3 stores none. */
  href?: string | null
  /** The printed form this edge was read from (`entry_people.src`), where it
   *  differs from the canonical name E2 wrote. Rule 5: the chip shows the
   *  canonical name and the printed one is a `title=` tooltip, nothing more. */
  src?: string | null
}

export interface Group { label: string; chips: Chip[] }

/** What `feed_detail.people` carries, per the E1/E3 migrations. */
export interface RawPerson {
  id: string
  name: string
  role: string
  src?: string | null
  description?: string | null
  born?: number | null
  died?: number | null
  wikipedia_url?: string | null
  /** Nightlife DJ facts (20260930190000): Discogs' own first sentence, and
   *  Discogs styles / MusicBrainz genres. Optional twice over: a detail
   *  cached before the migration has neither key, and most people have
   *  neither value — read `?? null` / `?? []`. */
  bio?: string | null
  genres?: string[] | null
}

/** What `feed_detail.ensembles` carries. No description, no Wikipedia: E3
 *  stores neither, so an ensemble chip is always a plain one. */
export interface RawEnsemble {
  id: string
  name: string
  role: string
  src?: string | null
}

/**
 * The order the groups are drawn in.
 *
 * `performer` and `guest` are `entry_ensembles.role`, and sit after
 * `conductor` because an orchestra reads as the thing the conductor is in
 * front of. The two namespaces are disjoint — `entry_people.role`'s CHECK
 * allows only the six person roles — so one ordered list answers for both
 * tables without a second key saying which.
 */
export const ROLE_ORDER: string[] = [
  // Nightlife N7: `dj` and `live` (N1's two new `entry_people` roles) sit
  // straight after `artist` — on a club night they are the whole list, and
  // the DJs lead because the bill does. No other kind of event carries them.
  'artist', 'dj', 'live', 'composer', 'author', 'conductor', 'performer', 'guest', 'director', 'curator',
]

/** Singular and plural, per role. `author` is "By" in both: "By Ibsen" reads
 *  as a credit where "Author" reads as a database column, and the stage
 *  normaliser files "Choreografie von X" here too. */
export const ROLE_LABEL: Record<string, [singular: string, plural: string]> = {
  artist: ['Artist', 'Artists'],
  dj: ['DJ', 'DJs'],
  // "Live" either way, as "By" is: "Live: Henrik Schwarz" reads as a bill,
  // and "Live acts" as a heading reads as a database column.
  live: ['Live', 'Live'],
  composer: ['Composer', 'Composers'],
  author: ['By', 'By'],
  conductor: ['Conductor', 'Conductors'],
  director: ['Director', 'Directors'],
  curator: ['Curator', 'Curators'],
  performer: ['Ensemble', 'Ensembles'],
  guest: ['With', 'With'],
}

/** How many chips a group shows before the rest go behind "+N more". A group
 *  show lists twelve artists and a fact list is not a wall label. */
export const CHIP_CAP = 8

/**
 * One line of fact under a matched person's chip, or null.
 *
 * Four shapes and nothing else: description with both years, description with
 * a birth year, a description alone, the years alone. Everything is E2's own
 * column, verbatim — the only decision here is the join.
 *
 * The one subtraction: Wikidata's English descriptions routinely print the
 * years themselves ("French photographer (1952–1983)", "Swedish painter (born
 * 1953)") — five of the eighteen matched people today. Appending the years to
 * one of those says the same fact twice on one line, which is the sheet with
 * two answers this repo keeps writing, so a description that already contains
 * the birth year stands alone.
 */
export function factLine(
  p: {
    description?: string | null; bio?: string | null; born?: number | null; died?: number | null
    role?: string | null; genres?: string[] | null
  },
): string | null {
  // Wikidata's description first, Discogs' first sentence when Wikidata has
  // none — the sheet's `description ?? bio`.
  const desc = p.description?.trim() || p.bio?.trim() || null
  const born = p.born ?? null
  const died = p.died ?? null
  const years = born && died ? `${born}–${died}` : born ? `b. ${born}` : died ? `d. ${died}` : null
  const base = desc && years && !desc.includes(String(born ?? died)) ? `${desc} · ${years}` : desc ?? years
  // A DJ's two leading styles, lowercased as the card's genre line is. In
  // front of the prose, not after it: the fact line is one line and
  // ellipsised, and a Discogs sentence is long enough that anything after
  // it would never be seen — the two words are the part a club sheet needs.
  const genres = p.role === 'dj' || p.role === 'live'
    ? (p.genres ?? []).map(g => (g ?? '').trim().toLowerCase()).filter(Boolean).slice(0, 2).join(', ') || null
    : null
  if (genres && base) return `${genres} · ${base}`
  return genres ?? base
}

/**
 * The chips on one event, grouped by role.
 *
 * Empty roles are omitted, chips keep the order the view returned them in
 * (`entry_people.position`, `entry_ensembles.position` — the order the house
 * printed them), and a role nothing in `ROLE_ORDER` names is kept at the end
 * rather than dropped: an unrecognised role is data, and dropping it would
 * lose a name off the screen without saying so.
 *
 * The cap is not applied here. `capChips` decides what is drawn now, because
 * "who is on this event" and "who is on screen while the group is collapsed"
 * are two different questions, and answering both with one array is this
 * codebase's own recurring bug.
 */
export function groupsFor(detail: {
  people?: RawPerson[] | null
  ensembles?: RawEnsemble[] | null
} | null | undefined): Group[] {
  const chips: Chip[] = []
  for (const p of detail?.people ?? []) {
    if (!p?.id || !p.role) continue
    chips.push({
      kind: 'person', id: p.id, name: p.name, role: p.role,
      fact: factLine(p),
      href: p.wikipedia_url ?? null,
      src: p.src && p.src !== p.name ? p.src : null,
    })
  }
  for (const s of detail?.ensembles ?? []) {
    if (!s?.id || !s.role) continue
    chips.push({
      kind: 'ensemble', id: s.id, name: s.name, role: s.role,
      fact: null, href: null,
      src: s.src && s.src !== s.name ? s.src : null,
    })
  }

  const byRole = new Map<string, Chip[]>()
  for (const c of chips) {
    const list = byRole.get(c.role)
    if (list) list.push(c); else byRole.set(c.role, [c])
  }

  const order = (role: string) => {
    const i = ROLE_ORDER.indexOf(role)
    return i < 0 ? ROLE_ORDER.length : i
  }
  return [...byRole.entries()]
    .sort((a, b) => order(a[0]) - order(b[0]))
    .map(([role, list]) => ({ label: labelFor(role, list.length), chips: list }))
}

/** The printed lineup `floorGroupsFor` reads — `NightFacts.lineup`, the
 *  fields it needs, declared structurally for this file's no-imports rule. */
export interface FloorSlot { name: string; role?: string; floor?: string; src?: string }

const key = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

/**
 * Nightlife N7: a club night's people grouped by the room they play in,
 * where the page split its lineup across more than one floor (Berghain and
 * Panorama Bar, Renate's colours, Tresor's three rooms) — or null, and the
 * sheet draws `groupsFor`'s DJs/Live groups as it does for anything else.
 *
 * The chips are `feed_detail.people` (they are what a tap filters by); the
 * floor is the lineup slot's. They meet on the printed form: `peopleFor`
 * (N2) writes each slot's `src ?? name` as the edge's `src`, and E2 may since
 * have renamed the person, so a person matches a slot by `src` first and by
 * name second. A floor with no names under it yet (Renate's "RED OPENS @")
 * is skipped rather than drawn as an empty heading; a person no floor claims
 * falls into the ordinary role groups after the floors, so nobody on the
 * edge list is lost off the sheet. A live act is marked in its chip's name,
 * because the group heading is the floor now and not the role.
 */
export function floorGroupsFor(
  detail: { people?: RawPerson[] | null; ensembles?: RawEnsemble[] | null } | null | undefined,
  night: { floors?: string[] | null; lineup?: FloorSlot[] | null } | null | undefined,
): Group[] | null {
  const floors = (night?.floors ?? []).filter(f => !!f?.trim())
  if (floors.length < 2) return null
  const people = (detail?.people ?? []).filter(p => p?.id && (p.role === 'dj' || p.role === 'live'))
  if (people.length === 0) return null

  const bySrc = new Map<string, RawPerson>()
  const byName = new Map<string, RawPerson>()
  for (const p of people) {
    if (p.src && !bySrc.has(key(p.src))) bySrc.set(key(p.src), p)
    if (!byName.has(key(p.name))) byName.set(key(p.name), p)
  }
  const personFor = (s: FloorSlot) =>
    bySrc.get(key(s.src ?? s.name)) ?? bySrc.get(key(s.name)) ?? byName.get(key(s.src ?? s.name)) ?? byName.get(key(s.name)) ?? null

  const placed = new Set<string>()
  const out: Group[] = []
  for (const floor of floors) {
    const chips: Chip[] = []
    const here = new Set<string>()
    for (const s of night?.lineup ?? []) {
      if (!s?.name || key(s.floor) !== key(floor)) continue
      const p = personFor(s)
      if (!p || here.has(p.id)) continue
      here.add(p.id)
      placed.add(p.id)
      chips.push({
        kind: 'person', id: p.id,
        name: p.role === 'live' ? `${p.name} (live)` : p.name,
        role: p.role,
        fact: factLine(p),
        href: p.wikipedia_url ?? null,
        src: p.src && p.src !== p.name ? p.src : null,
      })
    }
    if (chips.length) out.push({ label: floor.trim(), chips })
  }
  if (out.length === 0) return null

  const rest = groupsFor({
    people: (detail?.people ?? []).filter(p => !placed.has(p?.id)),
    ensembles: detail?.ensembles ?? [],
  })
  return [...out, ...rest]
}

/** A group's heading. An unknown role gets its own name back rather than a
 *  blank, for the same reason `groupsFor` keeps it. */
function labelFor(role: string, count: number): string {
  const pair = ROLE_LABEL[role]
  if (pair) return count > 1 ? pair[1] : pair[0]
  return role.charAt(0).toUpperCase() + role.slice(1)
}

/** What is on screen now, and how many are behind "+N more". */
export function capChips(chips: Chip[], expanded: boolean): { shown: Chip[]; hidden: number } {
  if (expanded || chips.length <= CHIP_CAP) return { shown: chips, hidden: 0 }
  return { shown: chips.slice(0, CHIP_CAP), hidden: chips.length - CHIP_CAP }
}

/**
 * The Browse query a chip tap writes (K6: a filter, not a person page).
 *
 * One key, never both — `?person=` and `?ensemble=` are the same axis asked
 * of two tables, and a URL carrying both would be two filters pretending to
 * be one.
 */
export function browseQuery(chip: Chip): Record<string, string> {
  return chip.kind === 'ensemble' ? { ensemble: chip.id } : { person: chip.id }
}
